import type { ReactNode } from 'react';
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthState } from './AuthContext';
import { useDOITimer } from '../hooks/useDOITimer';
import { authAPI } from '@/lib/api/auth';
import logger from '@/lib/utils/logger';

interface DOIContextValue {
  /** Whether DOI verification is required for this user */
  isDOIRequired: boolean;
  /** Whether the user is currently locked out (timer expired, not verified) */
  isLocked: boolean;
  /** Time remaining until lockout in seconds */
  timeRemaining: number;
  /** Formatted time remaining as "MM:SS" */
  formattedTime: string;
  /** Whether the DOI modal should be shown */
  isModalOpen: boolean;
  /** Function to resend the verification email */
  resendEmail: () => Promise<void>;
  /** Cooldown time remaining for resend button in seconds */
  resendCooldown: number;
  /** Whether a resend request is currently in progress */
  isResending: boolean;
  /** Whether the last resend was successful */
  resendSuccess: boolean;
}

const DOIContext = createContext<DOIContextValue | null>(null);

interface DOIProviderProps {
  children: ReactNode;
}

const RESEND_COOLDOWN_SECONDS = 60;

export function DOIProvider({ children }: DOIProviderProps) {
  const { user, isAuthenticated } = useAuthState();

  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Determine DOI configuration from user
  const doiConfig = user?.doi_config;
  const isVerified = user?.is_verified ?? true;

  // DOI is required if: user exists, not verified, and doi_config.required is true
  const isDOIRequired = Boolean(
    isAuthenticated &&
    user &&
    !isVerified &&
    doiConfig?.required
  );

  // Use the DOI timer hook
  const { timeRemaining, isLocked, formattedTime } = useDOITimer({
    registrationTimestamp: doiConfig?.registration_timestamp ?? null,
    delayMinutes: doiConfig?.delay_minutes ?? 0,
    enabled: isDOIRequired,
  });

  // Modal should be open if:
  // - User is locked out (timer expired), OR
  // - DOI is required AND delay is 0 (immediate verification required)
  const isModalOpen = Boolean(
    isDOIRequired && (isLocked || (doiConfig?.delay_minutes === 0))
  );

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [resendCooldown]);

  // Reset resend success after 3 seconds
  useEffect(() => {
    if (!resendSuccess) return;

    const timeout = setTimeout(() => {
      setResendSuccess(false);
    }, 3000);

    return () => clearTimeout(timeout);
  }, [resendSuccess]);

  // Resend verification email function
  const resendEmail = useCallback(async () => {
    if (resendCooldown > 0 || isResending) {
      return;
    }

    setIsResending(true);
    setResendSuccess(false);

    try {
      await authAPI.resendVerification();
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setResendSuccess(true);
      logger.info('DOI verification email resent successfully');
    } catch (error) {
      logger.error('Failed to resend DOI verification email:', error);
      // Still set a short cooldown to prevent spam even on error
      setResendCooldown(10);
    } finally {
      setIsResending(false);
    }
  }, [resendCooldown, isResending]);

  const value = useMemo<DOIContextValue>(
    () => ({
      isDOIRequired,
      isLocked,
      timeRemaining,
      formattedTime,
      isModalOpen,
      resendEmail,
      resendCooldown,
      isResending,
      resendSuccess,
    }),
    [
      isDOIRequired,
      isLocked,
      timeRemaining,
      formattedTime,
      isModalOpen,
      resendEmail,
      resendCooldown,
      isResending,
      resendSuccess,
    ]
  );

  return <DOIContext.Provider value={value}>{children}</DOIContext.Provider>;
}

/**
 * Hook to access DOI verification context.
 * Must be used within a DOIProvider.
 *
 * @throws Error if used outside of DOIProvider
 */
export function useDOI(): DOIContextValue {
  const context = useContext(DOIContext);
  if (!context) {
    throw new Error('useDOI must be used within a DOIProvider');
  }
  return context;
}
